import {
  createAsyncThunk,
  createEntityAdapter,
  createSlice,
  Dictionary,
  EntityId,
  EntityState,
} from '@reduxjs/toolkit';
import { apiClient } from 'api';
import {
  CreateSeedTicketData,
  GetProductionItemList,
  UpdateSeedReservationData,
} from 'api/productionItem';
import { GetSeedingBatchList, UpdateSeedingScheduleData } from 'api/seedingBatch';
import { ConfirmSeedTicketData, GetSeedTicketList } from 'api/seedTicket';
import {
  ProductionItem, ProductionItemDetail,
} from 'constants/interface';
import { RootState } from 'state';

const productionItemListEntityAdapter = createEntityAdapter<ProductionItem>();
const productionItemDetailEntityAdapter = createEntityAdapter<ProductionItemDetail>();

export interface ProductionItemListState extends EntityState<ProductionItem> {
  isLoading: boolean
  remoteTotal: number
}

export interface ProductionItemDetailState extends EntityState<ProductionItemDetail> {
  isLoading: boolean
}

export interface ProductionItemsState {
  list: ProductionItemListState
  detail: ProductionItemDetailState
}

export const initialState: ProductionItemsState = {
  list: productionItemListEntityAdapter.getInitialState({
    isLoading: false,
    remoteTotal: 0,
  }),
  detail: productionItemDetailEntityAdapter.getInitialState({
    isLoading: false,
  }),
};

export const getProductionItemAction = createAsyncThunk(
  'productionItems/detail',
  async (payload: {
    productionItemId: string,
  }) => {
    const { productionItemId } = payload;
    const response = await apiClient.productionItem.getProductionItem(productionItemId);

    return response;
  },
);

export const getProductionItemListAction = createAsyncThunk(
  'productionItems/list',
  async (payload: GetProductionItemList) => {
    const response = await apiClient
      .productionItem
      .getProductionItemList(payload);

    return response;
  },
);

export const getProductionItemSeedStatusAction = createAsyncThunk(
  'productionItems/seedStatus/get',
  async (payload: {
    productionItemId: string,
  }) => {
    const { productionItemId } = payload;
    const response = await apiClient.productionItem.getProductionItemSeedStatus(productionItemId);

    return response;
  },
);

export const getProductionItemAvailableSeedBatchesAction = createAsyncThunk(
  'productionItems/seedBatches/get',
  async (payload: {
    productionItemId: string,
  }) => {
    const { productionItemId } = payload;
    const response = await apiClient
      .productionItem
      .getProductionItemAvailableSeedBatches(productionItemId);

    return response;
  },
);

export const createSeedTicketAction = createAsyncThunk(
  'seedTicket/create',
  async (payload: CreateSeedTicketData) => {
    const seedTicketData = await apiClient.productionItem.createSeedTicket(payload);
    return seedTicketData;
  },
);

export const getSeedTicketListAction = createAsyncThunk(
  'seedTicket/list',
  async (payload: GetSeedTicketList) => {
    const seedTicketData = await apiClient.seedTicket.getSeedTicketList(payload);
    return seedTicketData;
  },
);

export const getSeedTicketDetailAction = createAsyncThunk(
  'seedTicket/detail',
  async (payload: {
    seedTicketId: string,
  }) => {
    const { seedTicketId } = payload;
    const seedTicketData = await apiClient.seedTicket.getSeedTicket(seedTicketId);
    return seedTicketData;
  },
);

export const confirmSeedTicketAction = createAsyncThunk(
  'seedTicket/confirm',
  async (payload: ConfirmSeedTicketData) => {
    const seedTicketData = await apiClient.seedTicket.confirmSeedTicket(payload);
    return seedTicketData;
  },
);

export const updateSeedingScheduleAction = createAsyncThunk(
  'seedingSchedule/update',
  async (payload: UpdateSeedingScheduleData) => {
    const seedingBatchData = await apiClient.seedingBatch.updateSeedingSchedule(payload);
    return seedingBatchData;
  },
);

export const updateSeedReservationAction = createAsyncThunk(
  'seedReservation/update',
  async (payload: UpdateSeedReservationData) => {
    const productionItemData = await apiClient.productionItem.updateSeedReservation(payload);
    return productionItemData;
  },
);

// TODO: experimenting with directly calling this on components
export const getSeedingBatchListAction = createAsyncThunk(
  'seedingBatch/list',
  async (payload: GetSeedingBatchList) => {
    const response = await apiClient.seedingBatch.getSeedingBatchList(payload);
    return response;
  },
);

export const productionItemsSlice = createSlice({
  name: 'productionItems',
  initialState,
  reducers: {
    cleanProductionItemsAction: (state) => {
      productionItemListEntityAdapter.removeAll(state.list);
    },
    cleanProductionItemsDetailAction: (state) => {
      productionItemDetailEntityAdapter.removeAll(state.detail);
    },
  },
  extraReducers: (builder) => {
    // List
    builder.addCase(getProductionItemListAction.pending, (state) => {
      state.list.isLoading = true;
    });
    builder.addCase(getProductionItemListAction.fulfilled, (state, action) => {
      productionItemListEntityAdapter.removeAll(state.list);
      productionItemListEntityAdapter.addMany(state.list, action.payload.data);
      state.list.remoteTotal = action.payload.pagination.total;
      state.list.isLoading = false;
    });
    builder.addCase(getProductionItemListAction.rejected, (state) => {
      state.list.isLoading = false;
    });

    // Single
    builder.addCase(getProductionItemAction.pending, (state, action) => {
      productionItemDetailEntityAdapter.removeOne(state.detail, action.meta.arg.productionItemId);
      state.detail.isLoading = true;
    });
    builder.addCase(getProductionItemAction.fulfilled, (state, action) => {
      // productionItemDetailEntityAdapter.removeOne(state.detail, action.payload.id);
      productionItemDetailEntityAdapter.upsertOne(state.detail, action.payload);
      state.detail.isLoading = false;
    });
    builder.addCase(getProductionItemAction.rejected, (state) => {
      state.detail.isLoading = false;
    });

    // Update seeding schedule
    // builder.addCase(updateSeedingScheduleAction.pending, (state) => {
    //   state.isLoading = true;
    // });
    // builder.addCase(getProductionItemAction.fulfilled, (state, action) => {
    //   // productionItemEntityAdapter.removeAll(state);
    //   // productionItemEntityAdapter.addMany(state, action.payload.data);
    //   productionItemEntityAdapter.removeOne(state, action.payload.id);
    //   productionItemEntityAdapter.addOne(state, action.payload);
    //   state.isLoading = false;
    // });
    // builder.addCase(getProductionItemAction.rejected, (state) => {
    //   state.isLoading = false;
    // });
  },
});

export const {
  cleanProductionItemsAction,
} = productionItemsSlice.actions;

// Selectors

export const isLoadingProductionItemsSelector = (state: RootState):
  boolean => state.productionItems.list.isLoading;

export const productionItemsEntitiesSelector = (state: RootState):
  Dictionary<ProductionItem> => state.productionItems.list.entities;

export const productionItemsIdsSelector = (state: RootState):
  EntityId[] => state.productionItems.list.ids;

export const remoteTotalSelector = (state: RootState):
  number => state.productionItems.list.remoteTotal;

export const isLoadingProductionItemDetailSelector = (state: RootState):
  boolean => state.productionItems.list.isLoading;

export const productionItemDetailEntitiesSelector = productionItemDetailEntityAdapter
  .getSelectors((state: RootState) => state.productionItems.detail)
  .selectEntities;

export const productionItemDetailIdsSelector = productionItemDetailEntityAdapter
  .getSelectors((state: RootState) => state.productionItems.detail)
  .selectIds;
